"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";

import { PlayButton } from "@/components/ui/play-button";
import { cn } from "@/lib/utils";

const MuxPlayer = dynamic(
  () =>
    import("@/components/post-rich-text/mux-player").then(
      (module) => module.MuxPlayer
    ),
  { ssr: false }
);

/** Length of the muted looping preview before the viewer presses play. */
const PREVIEW_SECONDS = 6;

/**
 * Autoplays a short muted snippet while on screen; pressing play swaps in the
 * full Mux player with sound.
 */
export function PreviewVideo({
  playbackId,
  title,
  poster,
  className,
}: {
  playbackId: string;
  title: string;
  poster: string;
  className?: string;
}) {
  const [playing, setPlaying] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const previewRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = previewRef.current;
    if (!video || playing) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.4 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [playing]);

  return (
    <div
      className={cn(
        "rounded-ds-xl bg-grey-950 relative aspect-video w-full overflow-hidden",
        className
      )}
    >
      {playing ? (
        <MuxPlayer
          playbackId={playbackId}
          title={title}
          poster={poster}
          autoPlay="any"
          loading="page"
          preload="auto"
          className="aspect-video w-full"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play ${title} video`}
          className="group focus-visible:ring-pulse-700 absolute inset-0 cursor-pointer focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
        >
          <video
            ref={previewRef}
            muted
            loop
            playsInline
            controls={false}
            preload="metadata"
            poster={poster}
            aria-hidden
            tabIndex={-1}
            onPlaying={() => setPreviewing(true)}
            onPause={() => setPreviewing(false)}
            onTimeUpdate={(event) => {
              const video = event.currentTarget;
              if (video.currentTime >= PREVIEW_SECONDS) video.currentTime = 0;
            }}
            className={cn(
              "absolute inset-0 h-full w-full object-cover",
              !previewing && "opacity-0"
            )}
          >
            <source
              src={`https://stream.mux.com/${playbackId}/medium.mp4`}
              type="video/mp4"
            />
          </video>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={poster}
            alt=""
            className={cn(
              "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
              previewing && "opacity-0"
            )}
          />
          <PlayButton className="absolute top-1/2 left-1/2 z-10 size-10 -translate-x-1/2 -translate-y-1/2 md:size-14" />
        </button>
      )}
    </div>
  );
}
